import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "content"]

  connect() {
    this.tooltip = null
    this.boundShow = this.showTitle.bind(this)
    this.boundHide = this.hideTitle.bind(this)

    // Title-based tooltip (legacy behavior)
    if (!this.hasContentTarget) {
      this.element.addEventListener('mouseenter', this.boundShow)
      this.element.addEventListener('mouseleave', this.boundHide)

      // Store original title immediately and remove it to prevent native tooltip
      this.originalTitle = this.element.getAttribute('title')
      if (this.originalTitle) {
        this.element.removeAttribute('title')
      }
    } else {
      // Content-based tooltip: detect if it uses fixed positioning
      try {
        this.usesFixedPositioning = this.contentTarget && this.contentTarget.classList.contains('fixed')
      } catch (e) {
        this.usesFixedPositioning = false
      }
      
      // Only add click-outside handler for fixed positioned tooltips
      if (this.usesFixedPositioning) {
        this.boundClickOutside = this.handleClickOutside.bind(this)
        document.addEventListener('click', this.boundClickOutside)
      }
    }
  }

  disconnect() {
    this.hideTitle()
    if (!this.hasContentTarget) {
      this.element.removeEventListener('mouseenter', this.boundShow)
      this.element.removeEventListener('mouseleave', this.boundHide)
      // Restore title on disconnect
      if (this.originalTitle) {
        this.element.setAttribute('title', this.originalTitle)
      }
    }

    if (this.usesFixedPositioning && this.boundClickOutside) {
      document.removeEventListener('click', this.boundClickOutside)
    }
  }

  // Content-based tooltip methods
  show() {
    if (!this.hasContentTarget) return
    
    clearTimeout(this.hideTimeout)
    this.contentTarget.classList.remove('hidden')
    
    // Only dynamically position if using fixed positioning and button target exists
    if (this.usesFixedPositioning && this.hasButtonTarget) {
      requestAnimationFrame(() => this.positionContentTooltip())
    }
  }

  hide() {
    if (!this.hasContentTarget) return
    
    clearTimeout(this.hideTimeout)
    this.contentTarget.classList.add('hidden')
  }

  toggle() {
    if (!this.hasContentTarget) return
    
    const isHidden = this.contentTarget.classList.contains('hidden')
    if (isHidden) {
      this.show()
    } else {
      this.hide()
    }
  }

  handleClickOutside(event) {
    // Only close if clicking outside this element
    if (!this.element.contains(event.target)) {
      this.hide()
    }
  }

  positionContentTooltip() {
    if (!this.hasContentTarget || !this.hasButtonTarget) return

    try {
      const buttonRect = this.buttonTarget.getBoundingClientRect()
      const tooltipRect = this.contentTarget.getBoundingClientRect()

      // Position below the button by default
      let top = buttonRect.bottom + 8
      let left = buttonRect.left + (buttonRect.width / 2) - (tooltipRect.width / 2)

      // Keep tooltip within viewport horizontally
      if (left < 8) {
        left = 8
      } else if (left + tooltipRect.width > window.innerWidth - 8) {
        left = window.innerWidth - tooltipRect.width - 8
      }

      this.contentTarget.style.top = `${top}px`
      this.contentTarget.style.left = `${left}px`
    } catch (e) {
      console.error('Error positioning tooltip:', e)
    }
  }

  // Title-based tooltip methods (legacy)
  showTitle() {
    if (this.tooltip || !this.originalTitle) return

    // Create tooltip element
    this.tooltip = document.createElement('div')
    this.tooltip.className = 'fixed z-[9999] bg-slate-800 text-white text-xs rounded-lg py-2 px-3 shadow-lg max-w-xs pointer-events-none whitespace-pre-wrap'
    this.tooltip.textContent = this.originalTitle
    document.body.appendChild(this.tooltip)

    // Position tooltip after adding to DOM
    requestAnimationFrame(() => this.positionTooltip())
  }

  hideTitle() {
    if (this.tooltip) {
      this.tooltip.remove()
      this.tooltip = null
    }
  }

  positionTooltip() {
    if (!this.tooltip) return

    const rect = this.element.getBoundingClientRect()
    const tooltipRect = this.tooltip.getBoundingClientRect()

    // Position above the element by default
    let top = rect.top - tooltipRect.height - 8
    let left = rect.left + (rect.width / 2) - (tooltipRect.width / 2)

    // If tooltip would go above viewport, show below
    if (top < 8) {
      top = rect.bottom + 8
    }

    // Keep tooltip within viewport horizontally
    if (left < 8) {
      left = 8
    } else if (left + tooltipRect.width > window.innerWidth - 8) {
      left = window.innerWidth - tooltipRect.width - 8
    }

    this.tooltip.style.top = `${top}px`
    this.tooltip.style.left = `${left}px`
  }
}
